import httpClient from 'api/https-client';
import { MouseEvent, useCallback } from 'react';
import { COACHESONLINE_MVP_SHORTCODE, urlHasCoachesOnlineShortcode } from 'utils/handoffShortcodes';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

// Re-exported for existing imports; the matching lives in utils/handoffShortcodes.
export { COACHESONLINE_MVP_SHORTCODE, urlHasCoachesOnlineShortcode };

const hrefHasShortcode = (anchor: HTMLAnchorElement): boolean =>
  urlHasCoachesOnlineShortcode(anchor.getAttribute('href') || '') || urlHasCoachesOnlineShortcode(anchor.href || '');

export const useCoachesOnlineHandoff = () => {
  const openHandoff = useCallback(async () => {
    try {
      // Mint a FRESH, short-lived (10 min) handoff URL on every click — never cache it.
      // The endpoint is gold/MVP-gated server-side (403 for everyone else → this no-ops).
      const { status, data } = await httpClient.get<{ url: string }>('/coachesonline/handoff-url');

      if (status >= 200 && status < 300 && data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      // Graceful degrade — surface nothing scary to the member; logged for debugging.
      console.error('Failed to build CoachesOnline handoff URL', error);
    }
  }, []);

  /**
   * Delegated click handler — attach to the element that renders manual content.
   * Intercepts clicks on any link whose URL contains the CoachesOnline shortcode and
   * swaps the dead shortcode link for a freshly minted, authenticated handoff.
   */
  const handleManualClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;

      if (!anchor || !hrefHasShortcode(anchor)) {
        return;
      }

      event.preventDefault();
      trackAmplitudeEvent('CoachesOnline Claim Pressed');
      void openHandoff();
    },
    [openHandoff],
  );

  return { handleManualClick };
};
